import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check } from "lucide-react";

export const TIERS = [
  { key: "starter", name: "Starter", tagline: "Solo professionals", seats: "1 digital card",
    monthly: { price: "$4.99", lookup: "starter_monthly" }, yearly: { price: "$49.90", lookup: "starter_yearly" },
    features: ["Mobile-responsive digital card", "vCard (.vcf) download", "Live QR code generator", "Email signature generator", "View & save analytics"] },
  { key: "team", name: "Team", tagline: "Small teams & agencies", seats: "5 user seats", popular: true,
    monthly: { price: "$9.99", lookup: "team_monthly" }, yearly: { price: "$99.90", lookup: "team_yearly" },
    features: ["Everything in Starter", "Client brand logo & hex colors", "Portfolio gallery + product showcase", "Lead capture + CSV export", "NFC tag & smart card studio", "Team directory page"] },
  { key: "enterprise", name: "Enterprise", tagline: "Corporations & franchises", seats: "25 seats",
    monthly: { price: "$29.99", lookup: "enterprise_monthly" }, yearly: { price: "$299.90", lookup: "enterprise_yearly" },
    features: ["Everything in Team", "Custom domain & white-label", "Bulk CSV import + undo", "AI copywriter (Gemini)", "CRM lead webhooks + alerts", "Security & audit logging"] },
];

export default function PricingTiers() {
  const { user } = useAuth();
  const [annual, setAnnual] = useState(false);

  const checkout = async (tier) => {
    const lookup = annual ? tier.yearly.lookup : tier.monthly.lookup;
    if (!user) { window.location.href = "/register"; return; }
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-center gap-4 mb-10">
        <span className={`text-sm ${!annual ? "text-[#F6F1E4]" : "text-[#8B9BB4]"}`}>Monthly</span>
        <button onClick={() => setAnnual(!annual)} data-testid="billing-toggle"
          className="relative h-7 w-14 rounded-full transition-colors" style={{ background: annual ? "#D4AF37" : "#142B5A" }}>
          <span className="absolute top-1 h-5 w-5 rounded-full bg-white transition-all" style={{ left: annual ? "34px" : "4px" }} />
        </button>
        <span className={`text-sm ${annual ? "text-[#F6F1E4]" : "text-[#8B9BB4]"}`}>Annual</span>
        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30">2 months free</span>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {TIERS.map((t, i) => {
          const plan = annual ? t.yearly : t.monthly;
          return (
            <div key={t.key} className={`iw-card p-8 relative fade-up ${t.popular ? "border-[#D4AF37]" : ""}`} style={{ animationDelay: `${i * 0.1}s` }} data-testid={`tier-${t.key}`}>
              {t.popular && <span className="absolute -top-3 left-8 bg-[#D4AF37] text-[#081426] text-xs font-bold px-3 py-1 rounded-full">MOST POPULAR</span>}
              <p className="overline">{t.tagline}</p>
              <h3 className="font-display text-2xl font-bold mt-2">{t.name}</h3>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-display text-4xl font-bold text-[#D4AF37]" data-testid={`price-${t.key}`}>{plan.price}</span>
                <span className="text-sm text-[#8B9BB4] mb-1.5">/ {annual ? "year" : "month"}</span>
              </div>
              {annual && (() => {
                const m = parseFloat(t.monthly.price.replace("$", ""));
                const y = parseFloat(t.yearly.price.replace("$", ""));
                const save = (m * 12 - y).toFixed(2);
                return (
                  <div className="mt-1.5" data-testid={`savings-${t.key}`}>
                    <p className="text-xs text-[#8B9BB4]">≈ {t.monthly.price}/mo billed yearly</p>
                    <span className="inline-block mt-1.5 text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                      Save ${save}/year
                    </span>
                  </div>
                );
              })()}
              <p className="text-sm text-[#F6F1E4] mt-2 font-semibold">{t.seats}</p>
              <ul className="mt-5 space-y-2.5">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-[#8B9BB4]">
                    <Check size={16} className="text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={2} /> {f}
                  </li>
                ))}
              </ul>
              <button onClick={() => checkout(t)} data-testid={`select-${t.key}`}
                className={`w-full mt-7 py-3 rounded-full ${t.popular ? "btn-gold" : "btn-ghost"}`}>
                Choose {t.name}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
